import type {
  FormAppearanceDto,
  FormAppearanceKey,
  FormAppearanceOverrides,
  FormAppearanceResolved,
  FormSaveAppearance,
  FormTheme,
  FormThemesCatalog,
} from "../../types/appearance";
import { getPublicMediaUrl } from "../media/getPublicMediaUrl";
import { parseMediaNodeId } from "../media/parseMediaNodeId";

export const APPEARANCE_KEYS: FormAppearanceKey[] = [
  "backgroundColor",
  "backgroundImage",
  "textColor",
  "headingColor",
  "labelColor",
  "accentColor",
  "errorColor",
  "fontFamily",
  "borderRadius",
  "maxWidth",
];

export const DEFAULT_THEME_STYLES: FormAppearanceResolved = {
  backgroundColor: "#ffffff",
  backgroundImage: "",
  textColor: "#1a1a1a",
  headingColor: "#111111",
  labelColor: "#4a4a4a",
  accentColor: "#4a6cf7",
  errorColor: "#e53e3e",
  fontFamily: "inherit",
  borderRadius: "8px",
  maxWidth: "640px",
};

export const FALLBACK_FORM_THEMES: FormThemesCatalog = {
  defaultTheme: "default",
  keys: APPEARANCE_KEYS,
  themes: [
    {
      id: "default",
      label: "Светлая",
      styles: { ...DEFAULT_THEME_STYLES },
    },
    {
      id: "soft",
      label: "Мягкая",
      styles: {
        backgroundColor: "#f8f9fb",
        backgroundImage: "",
        textColor: "#2d3748",
        headingColor: "#1a202c",
        labelColor: "#4a5568",
        accentColor: "#667eea",
        errorColor: "#e53e3e",
        fontFamily: "inherit",
        borderRadius: "10px",
        maxWidth: "640px",
      },
    },
    {
      id: "church-blue",
      label: "Церковная синяя",
      styles: {
        backgroundColor: "#eef3fa",
        backgroundImage: "",
        textColor: "#1e2a3a",
        headingColor: "#0f1f33",
        labelColor: "#3d5166",
        accentColor: "#1d4e89",
        errorColor: "#c53030",
        fontFamily: "inherit",
        borderRadius: "8px",
        maxWidth: "640px",
      },
    },
    {
      id: "warm",
      label: "Тёплая",
      styles: {
        backgroundColor: "#fff0e6",
        backgroundImage: "",
        textColor: "#3d2c1f",
        headingColor: "#2a1d12",
        labelColor: "#5c4a3a",
        accentColor: "#c45c26",
        errorColor: "#d9480f",
        fontFamily: "inherit",
        borderRadius: "10px",
        maxWidth: "640px",
      },
    },
    {
      id: "dark",
      label: "Тёмная",
      styles: {
        backgroundColor: "#1a1d24",
        backgroundImage: "",
        textColor: "#e8eaed",
        headingColor: "#ffffff",
        labelColor: "#b0b8c4",
        accentColor: "#6b9fff",
        errorColor: "#fc8181",
        fontFamily: "inherit",
        borderRadius: "10px",
        maxWidth: "640px",
      },
    },
  ],
};

export function findTheme(
  themeId: string,
  themes: FormTheme[] = FALLBACK_FORM_THEMES.themes,
): FormTheme | undefined {
  return themes.find((theme) => theme.id === themeId);
}

export function mergeAppearance(
  themeId: string,
  overrides: FormAppearanceOverrides,
  themes: FormTheme[] = FALLBACK_FORM_THEMES.themes,
): FormAppearanceResolved {
  const base =
    findTheme(themeId, themes)?.styles ??
    findTheme(FALLBACK_FORM_THEMES.defaultTheme, themes)?.styles ??
    DEFAULT_THEME_STYLES;

  const merged = { ...base };
  for (const key of APPEARANCE_KEYS) {
    const value = overrides[key];
    if (value != null && String(value).trim() !== "") {
      merged[key] = String(value).trim();
    }
  }
  return merged;
}

function pickOverridesFromFlat(
  appearance: FormAppearanceDto,
): FormAppearanceOverrides {
  const overrides: FormAppearanceOverrides = {};
  for (const key of APPEARANCE_KEYS) {
    const value = appearance[key];
    if (value != null && String(value).trim() !== "") {
      overrides[key] = String(value).trim();
    }
  }
  const nodeId = parseMediaNodeId(appearance.backgroundImageNodeId);
  if (nodeId != null && overrides.backgroundImage) {
    overrides.backgroundImageNodeId = nodeId;
  }
  return overrides;
}

export function appearanceOverridesFromDto(
  appearance?: FormAppearanceDto | null,
): FormAppearanceOverrides {
  if (!appearance) return {};

  if (appearance.overrides && typeof appearance.overrides === "object") {
    const overrides: FormAppearanceOverrides = {};
    for (const key of APPEARANCE_KEYS) {
      const value = appearance.overrides[key];
      if (value != null && String(value).trim() !== "") {
        overrides[key] = String(value).trim();
      }
    }
    const nodeId = parseMediaNodeId(appearance.overrides.backgroundImageNodeId);
    if (nodeId != null && overrides.backgroundImage) {
      overrides.backgroundImageNodeId = nodeId;
    }
    return overrides;
  }

  return pickOverridesFromFlat(appearance);
}

export function resolveAppearanceForRender(
  appearance: FormAppearanceDto | null | undefined,
  themes: FormTheme[] = FALLBACK_FORM_THEMES.themes,
): FormAppearanceResolved {
  if (appearance?.resolved) {
    return normalizeResolvedStyles(appearance.resolved);
  }

  const themeId =
    appearance?.theme?.trim() || FALLBACK_FORM_THEMES.defaultTheme;
  const overrides = appearanceOverridesFromDto(appearance);
  return mergeAppearance(themeId, overrides, themes);
}

function normalizeResolvedStyles(
  resolved: Partial<FormAppearanceResolved>,
): FormAppearanceResolved {
  const styles = { ...DEFAULT_THEME_STYLES };
  for (const key of APPEARANCE_KEYS) {
    const value = resolved[key];
    if (value != null) {
      styles[key] = String(value);
    }
  }
  if (!styles.backgroundImage) styles.backgroundImage = "";
  return styles;
}

export function buildAppearanceSavePayload(
  themeId: string,
  overrides: FormAppearanceOverrides,
  cleared: boolean,
  themes: FormTheme[] = FALLBACK_FORM_THEMES.themes,
): FormSaveAppearance {
  if (cleared) return null;

  const base = mergeAppearance(themeId, {}, themes);
  const payload: FormSaveAppearance = { theme: themeId };

  for (const key of APPEARANCE_KEYS) {
    const value = overrides[key];
    if (value == null || String(value).trim() === "") continue;
    const trimmed = String(value).trim();
    if (trimmed !== base[key]) {
      payload[key] = trimmed;
    }
  }

  const nodeId = parseMediaNodeId(overrides.backgroundImageNodeId);
  if (payload.backgroundImage && nodeId != null) {
    payload.backgroundImageNodeId = nodeId;
  }

  return payload;
}

export function formAppearanceCssVars(
  styles: FormAppearanceResolved,
): Record<string, string> {
  const imageUrl = styles.backgroundImage
    ? getPublicMediaUrl(styles.backgroundImage)
    : "";

  return {
    "--form-bg": styles.backgroundColor,
    "--form-text": styles.textColor,
    "--form-heading": styles.headingColor,
    "--form-label": styles.labelColor,
    "--form-accent": styles.accentColor,
    "--form-error": styles.errorColor,
    "--form-radius": styles.borderRadius,
    "--form-font": styles.fontFamily,
    "--form-bg-image": imageUrl ? `url("${imageUrl}")` : "none",
  };
}

export const APPEARANCE_KEY_LABELS: Record<FormAppearanceKey, string> = {
  backgroundColor: "Фон",
  backgroundImage: "Фоновая картинка",
  textColor: "Текст",
  headingColor: "Заголовки",
  labelColor: "Подписи полей",
  accentColor: "Акцент",
  errorColor: "Ошибки",
  fontFamily: "Шрифт",
  borderRadius: "Скругление",
  maxWidth: "Ширина",
};

export function isColorAppearanceKey(key: FormAppearanceKey): boolean {
  return key.endsWith("Color");
}
